/**
 * Application Configuration
 * Single source of truth for all app settings
 * Environment configurable for different deployments
 */

export const config = {
  // API Configuration
  // NOTE: `??` (not `||`) — an empty VITE_API_URL must stay "" (same-origin,
  // relative) for prod builds, NOT fall back to localhost. With `||`, an empty
  // value collapsed to http://localhost:3000, which on the HTTPS prod page is a
  // mixed-content request → "Failed to fetch" → the UIKit client shows
  // "Backend not available" (broke TPO/email login only; student login already
  // used `??` in shared/lib/api.ts so it was unaffected).
  api: {
    baseUrl: import.meta.env.VITE_API_URL ?? 'http://localhost:3000',
    endpoints: {
      login: '/api/auth/login',
      register: '/api/auth/register',
      verifyEmail: '/api/auth/verify-email',
      resendVerification: '/api/auth/resend-verification',
      forgotPassword: '/api/auth/forgot-password',
      resetPassword: '/api/auth/reset-password',
      profile: '/api/user/profile',
      updateProfile: '/api/user/profile',
    }
  },

  // Auth Configuration
  auth: {
    // Storage keys
    storage: {
      token: import.meta.env.VITE_AUTH_TOKEN_KEY || 'auth_token',
      user: import.meta.env.VITE_AUTH_USER_KEY || 'auth_user',
    },

    // Headers
    headers: {
      frontendKey: 'X-Frontend-Key',
      frontendKeyValue: import.meta.env.VITE_FRONTEND_KEY || '',
      auth: 'Authorization',
    },

    // Redirect configuration - environment configurable.
    // Defaults match the actual FBCA auto-discovered routes: pages under
    // `features/auth/pages/` resolve to `/auth/*`, not `/*`. Override via
    // env if you relocate the pages.
    redirects: {
      // Where to send unauthenticated visitors who land on a protected
      // page — the login form directly (not the marketing homepage), so
      // white-label deployments never flash the landing at signed-out users.
      loginPage: import.meta.env.VITE_LOGIN_PAGE || '/auth/login',

      // Where to send authenticated users after login
      afterLogin: import.meta.env.VITE_AFTER_LOGIN || '/dashboard',

      // Where to send after logout
      afterLogout: import.meta.env.VITE_AFTER_LOGOUT || '/',

      // Where to send authenticated users trying to access auth pages
      authenticatedRedirect: import.meta.env.VITE_AUTHENTICATED_REDIRECT || '/dashboard'
    }
  },

  // Route Access Control
  routes: {
    // Auth-only routes (redirect to dashboard if authenticated). These
    // paths must NOT include public marketing pages (/, /about, etc.) —
    // listing them here would bounce signed-in users away from the
    // homepage, which is the opposite of what you want on a marketing
    // site.
    authOnly: ['/auth/login', '/auth/register', '/auth/forgot-password', '/auth/reset-password', '/student/set-password', '/student/forgot-password'],

    // Public routes (accessible to everyone regardless of auth state).
    // `/` is the marketing homepage; `/apply` is the waitlist; `/accept`
    // is the invite-acceptance landing.
    public: [
      '/', '/about', '/help', '/contact', '/gallery', '/terms', '/privacy',
      '/refund', '/cancellation', '/security',
      // /support MUST stay public: everyone who needs it is locked out of
      // the app, so gating it behind the sign-in it exists to fix would be
      // circular.
      '/support',
      '/waitlist', '/contact-admin', '/npei', '/accept', '/placement-officer', '/for-students', '/exam-cover', '/ecb', '/blog', '/downloads', '/live',
      '/blog/how-tpos-manage-campus-placements',
      '/blog/student-shortlisting-for-placement-drives',
      '/blog/building-a-high-performing-placement-cell',
      '/blog/campus-placement-trends-india-2025',
      '/blog/campus-placement-management-software-guide',
      '/blog/how-to-improve-college-placement-percentage',
      '/blog/why-excel-is-holding-your-placement-cell-back',
      '/blog/how-to-get-more-companies-for-campus-placement',
      '/blog/how-to-run-aptitude-tests-for-campus-placement',
      '/blog/common-placement-cell-mistakes-and-how-to-fix-them',
      '/auth/verify-email', '/auth/forgot-password', '/auth/reset-password', '/auth/token-login',
    ],

    // Logout routes - special handling
    logout: ['/logout', '/auth/logout'],

    // Role-based routes (simple path matching)
    roles: {
      // Admin routes - require admin.* roles
      '/settings/team':    ['admin.tenant', 'admin.org', 'admin.system'],
      '/settings/college': ['admin.tenant', 'admin.org', 'admin.system'],

      // Moderator+ routes - moderators and admins can access
      // moderator.approve is the platform SUPPORT desk (see SUPPORT_DESK in
      // platform.shared.ts) — a support agent has no tenant, so no LLM usage.
      '/settings/usage':   ['moderator.review', 'moderator.manage', 'admin.tenant', 'admin.org', 'admin.system']
    }
  }
};